import { useState } from 'react';
import { fmt } from '../../utils/formatters';
import { api } from '../../services/apiClient';

/**
 * Displays sub-invoices generated from real receipt items (source='receipt')
 * and auto-added filler items (source='auto'), grouped into separate sections.
 * Each card is expandable — click it to see the full item table inline,
 * including a Discount column when the matched product price > receipt price.
 */
export default function SubInvoicesPanel({ subInvoices, invoiceId, onDeleteSubInvoice, onDeleteAllSubInvoices }) {
  if (!subInvoices || subInvoices.length === 0) return null;

  return (
    <div className="sub-invoices-panel no-print" style={{
      margin: '0 0 0',
      borderTop: '2px solid var(--gold-light, #c4963a)',
    }}>
      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 32px 10px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 16 }}>📋</span>
          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--gold, #8b6914)' }}>
            Sub-Invoices ({subInvoices.length})
          </span>
          <span style={{ fontSize: 12, color: 'var(--muted, #888)' }}>
            — each ≤ £100
          </span>
        </div>
        {invoiceId && onDeleteAllSubInvoices && (
          <button
            type="button"
            className="btn btn-sm btn-outline"
            style={{
              borderColor: 'var(--danger, #9b2335)',
              color: 'var(--danger, #9b2335)',
              padding: '4px 10px',
              fontSize: '11px',
              fontWeight: 600,
            }}
            onClick={() => onDeleteAllSubInvoices(invoiceId)}
          >
            🗑 Delete All Sub-Invoices
          </button>
        )}
      </div>

      <div style={{ padding: '0 32px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {subInvoices.map((sub, i) => (
          <SubInvoiceCard
            key={sub.invoiceNo || i}
            sub={sub}
            index={i}
            invoiceId={invoiceId}
            onDeleteSubInvoice={onDeleteSubInvoice}
          />
        ))}
      </div>
    </div>
  );
}

function SubInvoiceCard({ sub, index, invoiceId, onDeleteSubInvoice }) {
  const [open, setOpen] = useState(false);
  const hasItems = sub.items && sub.items.length > 0;
  const hasDiscount = hasItems && sub.items.some(it => Number(it.discount) > 0);

  return (
    <div style={{
      border: '1px solid var(--gold-light, #c4963a)',
      borderRadius: 10,
      overflow: 'hidden',
      background: 'var(--cream, #f5edd6)',
    }}>
      {/* ── Clickable header row ── */}
      <div
        onClick={() => hasItems && setOpen(o => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 18px',
          cursor: hasItems ? 'pointer' : 'default',
          userSelect: 'none',
          gap: 12,
        }}
      >
        {/* Left */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'var(--gold, #8b6914)',
            color: '#fff', fontSize: 12, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {index + 1}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--dark, #2c1810)' }}>
              {sub.invoiceNo}
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted, #888)', marginTop: 2 }}>
              {hasItems
                ? `${sub.items.length} item${sub.items.length !== 1 ? 's' : ''}${hasDiscount ? ' · incl. discount' : ''} · click to ${open ? 'collapse' : 'expand'}`
                : 'No items stored'}
            </div>
          </div>
        </div>

        {/* Right */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--gold, #8b6914)' }}>
              {fmt(sub.grandTotal)}
            </div>
            {sub.vatAmount > 0 && (
              <div style={{ fontSize: 11, color: 'var(--muted, #888)' }}>
                incl. {fmt(sub.vatAmount)} VAT
              </div>
            )}
          </div>

          {invoiceId && (
            <button
              type="button"
              className="btn-icon download"
              title="Download sub-invoice XLSX"
              onClick={e => {
                e.stopPropagation();
                window.open(api.subInvoiceXlsxUrl(invoiceId, index), '_blank');
              }}
            >
              📥
            </button>
          )}

          {invoiceId && onDeleteSubInvoice && (
            <button
              type="button"
              className="btn-icon"
              title="Delete sub-invoice"
              style={{ color: 'var(--danger, #9b2335)', cursor: 'pointer', fontSize: '15px' }}
              onClick={e => {
                e.stopPropagation();
                onDeleteSubInvoice(invoiceId, index);
              }}
            >
              🗑
            </button>
          )}

          {sub.driveWebViewLink && (
            <a
              href={sub.driveWebViewLink}
              target="_blank"
              rel="noreferrer"
              className="drive-badge"
              style={{ whiteSpace: 'nowrap', fontSize: 12 }}
              title="Open sub-invoice XLSX in Google Drive"
              onClick={e => e.stopPropagation()}
            >
              📁 Drive ↗
            </a>
          )}

          {hasItems && (
            <span style={{
              fontSize: 18, color: 'var(--gold, #8b6914)',
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              display: 'inline-block',
            }}>
              ▾
            </span>
          )}
        </div>
      </div>

      {/* ── Expandable items table ── */}
      {open && hasItems && (
        <div style={{ borderTop: '1px solid var(--gold-light, #c4963a)', background: '#fff', padding: '0 18px 18px' }}>
          <table className="items-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Description', 'Qty', 'Unit Price', 'VAT %', 'VAT Amount', hasDiscount ? 'Discount' : null, 'Total']
                  .filter(Boolean)
                  .map((h, idx) => (
                    <th key={h} className={idx === 0 ? '' : 'cell-center'} style={{
                      padding: '8px 12px',
                    }}>{h}</th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {sub.items.map((it, i) => {
                const lineTotal = Number(it.qty) * Number(it.unitPrice);
                const lineVat   = lineTotal * (Number(it.vatRate) / 100);
                const discount  = Number(it.discount) || 0;
                const displayUnitPrice = (discount > 0 && it.matchedProductPrice) ? it.matchedProductPrice : it.unitPrice;
                return (
                  <tr key={i} className={it.isAutoAdded ? 'auto-item' : ''}>
                    <td>
                      {it.description}
                      {it.isAutoAdded && <span className="auto-badge" style={{ marginLeft: 6 }}>auto</span>}
                    </td>
                    <td className="cell-center">{it.qty}</td>
                    <td className="cell-num">{fmt(displayUnitPrice)}</td>
                    <td className="cell-center">{it.vatRate}%</td>
                    <td className="cell-muted">{fmt(lineVat)}</td>
                    {hasDiscount && (
                      <td className="cell-num" style={{ color: discount > 0 ? 'var(--danger, #9b2335)' : 'inherit', fontWeight: discount > 0 ? 600 : 400 }}>
                        {discount > 0 ? `-${fmt(discount)}` : '—'}
                      </td>
                    )}
                    <td className="cell-gold">{fmt(lineTotal)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: 'var(--cream, #f5edd6)', borderTop: '1px solid var(--gold-light, #c4963a)' }}>
                <td colSpan={hasDiscount ? 6 : 5} style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, fontSize: 12, color: 'var(--dark, #2c1810)' }}>
                  Subtotal
                </td>
                <td className="cell-num" style={{ fontWeight: 700, color: 'var(--dark, #2c1810)' }}>
                  {fmt(sub.subtotal)}
                </td>
              </tr>
              {sub.vatAmount > 0 && (
                <tr style={{ background: 'var(--cream, #f5edd6)' }}>
                  <td colSpan={hasDiscount ? 6 : 5} style={{ padding: '4px 12px', textAlign: 'right', fontSize: 12, color: 'var(--muted, #888)' }}>
                    VAT
                  </td>
                  <td className="cell-muted" style={{ color: 'var(--muted, #888)' }}>
                    {fmt(sub.vatAmount)}
                  </td>
                </tr>
              )}
              <tr style={{ background: 'var(--gold, #8b6914)' }}>
                <td colSpan={hasDiscount ? 6 : 5} style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700, color: '#fff', fontSize: 13 }}>
                  Grand Total
                </td>
                <td className="cell-num" style={{ fontWeight: 700, color: '#fff', fontSize: 14 }}>
                  {fmt(sub.grandTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
